import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MaterialModule } from "src/app/app.materials";
import { CommonModule } from '@angular/common';

interface HeatmapCell {
  value: number | string;
  background: string;
  strong: boolean;
}

interface HeatmapRow {
  label: string;
  full: string;
  cells: HeatmapCell[];
}

@Component({
  selector: 'app-heatmap-table',
  templateUrl: './heatmap-table.component.html',
  styleUrls: ['./heatmap-table.component.scss'],
  imports: [MaterialModule, CommonModule],
  standalone: true,
})
export class HeatmapTableComponent implements OnChanges, OnInit {
  @Input() dataRaw: any = null;
  @Input() allowedTickers: any[] = [];
  /** Render with a +/- divergent ramp (correlation) instead of the purple magnitude ramp (covariance). */
  @Input() divergent = false;
  @Input() title: string;
  @Input() info: string;

  tableData: any[] = [];
  rows: HeatmapRow[] = [];
  colHeaders: { label: string; full: string }[] = [];

  hoverRow: number | null = null;
  hoverCol: number | null = null;

  ngOnInit(): void {
    this.process();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.allowedTickers || changes.dataRaw || changes.divergent) {
      this.process();
    }
  }

  private process() {
    if (!this.dataRaw?.length) {
      this.tableData = [];
      this.rows = [];
      this.colHeaders = [];
      return;
    }

    for (const row of this.dataRaw) {
      for (const cell of row) {
        cell.fullName1 = cell.Var1Full ?? cell.companyName1 ?? cell.Var1;
        cell.fullName2 = cell.Var2Full ?? cell.companyName2 ?? cell.Var2;
      }
    }

    this.filter();
    this.build();
  }

  private filter() {
    if (this.allowedTickers && this.allowedTickers.length) {
      const filtered = [];
      for (const rowData of this.dataRaw) {
        if (this.allowedTickers.includes(rowData[0].Var2Full)) {
          const iterCol = [];
          for (const data of rowData) {
            if (this.allowedTickers.includes(data.Var1Full)) {
              iterCol.push(data);
            }
          }
          filtered.push(iterCol);
        }
      }
      this.tableData = filtered;
    } else {
      this.tableData = this.dataRaw;
    }
  }

  private build() {
    const data = this.tableData;
    if (!data.length) {
      this.rows = [];
      this.colHeaders = [];
      return;
    }

    const absVals: number[] = [];
    for (const row of data) {
      for (const cell of row) {
        if (typeof cell.value === 'number') {
          absVals.push(Math.abs(cell.value));
        }
      }
    }
    const vmax = absVals.length ? Math.max(...absVals) : 1;
    const vmin = absVals.length ? Math.min(...absVals) : 0;
    const divMax = this.divergent ? Math.max(1, vmax) : vmax;

    this.colHeaders = data.map((row) => ({
      label: this.shortLabel(row[0].ticker2 ?? row[0].Var2, row[0].fullName2),
      full: row[0].fullName2,
    }));

    this.rows = data.map((row) => ({
      label: this.shortLabel(row[0].ticker2 ?? row[0].Var2, row[0].fullName2),
      full: row[0].fullName2,
      cells: row.map((cell) => this.makeCell(cell, vmin, vmax, divMax)),
    }));
  }

  private shortLabel(short: string, full: string): string {
    const value = (short ?? full ?? '').toString();
    return value.length > 12 ? value.slice(0, 12) + '…' : value;
  }

  private makeCell(cell: any, vmin: number, vmax: number, divMax: number): HeatmapCell {
    const v = typeof cell.value === 'number' ? cell.value : 0;
    let background: string;
    let strong: boolean;

    if (this.divergent) {
      const t = Math.max(-1, Math.min(1, v / (divMax || 1)));
      const amount = Math.round((0.12 + 0.7 * Math.min(1, Math.abs(t))) * 100);
      const token = t >= 0 ? '--pos' : '--neg';
      background = `color-mix(in srgb, var(${token}) ${amount}%, transparent)`;
      strong = Math.abs(t) > 0.5;
    } else {
      const t = Math.max(0, Math.min(1, (Math.abs(v) - vmin) / ((vmax - vmin) || 1)));
      const amount = Math.round(this.magnitudeAlpha(t) * 100);
      background = `color-mix(in srgb, var(--accent) ${amount}%, transparent)`;
      strong = t > 0.55;
    }

    return { value: cell.value, background, strong };
  }

  /** Eased alpha ramp (0.06 → 0.88) so low magnitudes stay readable. */
  private magnitudeAlpha(t: number): number {
    const stops = [0.06, 0.14, 0.22, 0.35, 0.5, 0.68, 0.88];
    const steps = [0, 1 / 6, 2 / 6, 3 / 6, 4 / 6, 5 / 6, 1];
    let alpha = stops[0];
    for (let i = 1; i < steps.length; i++) {
      if (t <= steps[i]) {
        const frac = (t - steps[i - 1]) / (steps[i] - steps[i - 1] || 1);
        alpha = stops[i - 1] + frac * (stops[i] - stops[i - 1]);
        break;
      }
    }
    return alpha;
  }

  get gridTemplate(): string {
    return `minmax(56px, max-content) repeat(${this.colHeaders.length}, minmax(44px, 96px))`;
  }

  setHover(row: number, col: number) {
    this.hoverRow = row;
    this.hoverCol = col;
  }

  clearHover() {
    this.hoverRow = null;
    this.hoverCol = null;
  }

  isCellDimmed(row: number, col: number): boolean {
    return this.hoverRow !== null && row !== this.hoverRow && col !== this.hoverCol;
  }

  isLabelDimmed(axis: 'row' | 'col', index: number): boolean {
    if (this.hoverRow === null) {
      return false;
    }
    return axis === 'row' ? index !== this.hoverRow : index !== this.hoverCol;
  }
}
